import React, { useState } from 'react';
import { motion } from "motion/react";
import { GoogleIcon, AppleIcon, FacebookIcon } from './SocialIcons';

interface SocialSignupButtonsProps {
  onSocialSignup: (provider: string) => void;
}

export const SocialSignupButtons: React.FC<SocialSignupButtonsProps> = ({ onSocialSignup }) => {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  const socialProviders = [
    {
      id: 'google',
      name: 'Sign up with Google',
      icon: GoogleIcon,
      bgColor: 'bg-white',
      textColor: 'text-gray-800'
    },
    {
      id: 'apple',
      name: 'Sign up with Apple',
      icon: AppleIcon,
      bgColor: 'bg-gray-900',
      textColor: 'text-white',
      borderColor: 'border-gray-700'
    },
    {
      id: 'facebook',
      name: 'Sign up with Facebook',
      icon: FacebookIcon,
      bgColor: 'bg-blue-600',
      textColor: 'text-white'
    }
  ];

  return (
    <motion.div 
      layout
      className="flex items-center justify-center gap-4 mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {socialProviders.map((provider, index) => {
        const IconComponent = provider.icon;
        const isHovered = hoveredButton === provider.id;
        
        return (
          <motion.button 
            key={provider.id}
            layout
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              flexGrow: isHovered ? 2 : 1
            }}
            transition={{ 
              duration: 0.3, 
              delay: index * 0.1,
              ease: "easeOut"
            }}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2, ease: "easeOut" }
            }}
            onClick={() => onSocialSignup(provider.id)}
            onMouseEnter={() => setHoveredButton(provider.id)}
            onMouseLeave={() => setHoveredButton(null)}
            className={`flex items-center justify-center h-14 rounded-2xl hover:cursor-pointer transition-all duration-300 ease-out ${provider.bgColor} ${provider.textColor} ${provider.borderColor || ''}`}
          >
            <div className="flex items-center">
              <motion.div 
                animate={{ 
                  scale: isHovered ? 1.15 : 1,
                  rotate: isHovered ? 5 : 0
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className={isHovered ? 'mr-3' : 'mx-auto'}
              >
                <IconComponent />
              </motion.div>
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ 
                  opacity: isHovered ? 1 : 0, 
                  width: isHovered ? 'auto' : 0,
                }}
                transition={{ 
                  duration: 0.3, 
                  ease: "easeOut"
                }}
                className="font-medium whitespace-nowrap overflow-hidden"
              >
                {provider.name}
              </motion.span>
            </div>
          </motion.button>
        );
      })}
    </motion.div>
  );
};
